import { useEffect, useState, type MouseEvent } from "react";
import { useRouter } from "@tanstack/react-router";
import { Bug, Check, Lightbulb, Loader2, MessageSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { TablePagination } from "@/components/ui/table-pagination";
import { cn, formatDateTime } from "@/lib/utils";
import {
  useBugReports,
  useSetBugReportStatus,
  useDeleteBugReport,
  type BugReport,
  type BugReportStatus,
  type BugReportType,
} from "@/hooks/use-bug-reports";
import { AttachmentStrip, AttachmentViewer } from "@/components/bug-reports/attachments";
import { Description } from "@/components/bug-reports/description";
import { TicketDialog } from "@/components/bug-reports/ticket-dialog";

type StatusFilter = BugReportStatus | "sve";
type TypeFilter = BugReportType | "sve";

const PER_PAGE = 25;

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: "otvoren", label: "Otvorene" },
  { value: "rijesen", label: "Riješene" },
  { value: "sve", label: "Sve" },
];

const TYPE_FILTERS: { value: TypeFilter; label: string }[] = [
  { value: "sve", label: "Sve vrste" },
  { value: "bug", label: "Bugovi" },
  { value: "prijedlog", label: "Prijedlozi" },
];

const EMPTY_COPY: Record<StatusFilter, Record<TypeFilter, string>> = {
  otvoren: {
    sve: "Nema otvorenih prijava — sve je riješeno.",
    bug: "Nema otvorenih bugova.",
    prijedlog: "Nema otvorenih prijedloga.",
  },
  rijesen: {
    sve: "Još nijedna prijava nije riješena.",
    bug: "Još nijedan bug nije riješen.",
    prijedlog: "Još nijedan prijedlog nije riješen.",
  },
  sve: {
    sve: "Još niko nije poslao prijavu.",
    bug: "Nema prijavljenih bugova.",
    prijedlog: "Nema poslanih prijedloga.",
  },
};

const MONTHS = [
  "januar", "februar", "mart", "april", "maj", "juni",
  "juli", "august", "septembar", "oktobar", "novembar", "decembar",
];

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Naslov grupe u listi: „Danas“, „Jučer“ ili puni datum. */
function dayLabel(d: Date): string {
  const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86_400_000);
  if (days === 0) return "Danas";
  if (days === 1) return "Jučer";
  const year = d.getFullYear() === new Date().getFullYear() ? "" : ` ${d.getFullYear()}.`;
  return `${d.getDate()}. ${MONTHS[d.getMonth()]}${year}`;
}

function timeLabel(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** Lista stiže sortirana po vremenu, pa su prijave istog dana uvijek susjedne. */
function groupByDay(items: BugReport[]): { label: string; items: BugReport[] }[] {
  const groups: { label: string; items: BugReport[] }[] = [];
  for (const item of items) {
    const label = dayLabel(new Date(item.createdAt * 1000));
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  }
  return groups;
}

/** Kvadratić ispred prijave — rješavanje je štikliranje, kao na spisku obaveza. */
function StatusBox({
  resolved,
  pending,
  onToggle,
}: {
  resolved: boolean;
  pending: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={resolved}
      aria-label={resolved ? "Ponovo otvori" : "Označi kao riješeno"}
      title={resolved ? "Ponovo otvori" : "Označi kao riješeno"}
      onClick={onToggle}
      disabled={pending}
      className={cn(
        "group/box mt-px flex size-5 shrink-0 items-center justify-center border outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none",
        resolved
          ? "border-foreground bg-foreground text-background hover:bg-foreground/70"
          : "border-foreground/50 hover:border-foreground",
      )}
    >
      {pending ? (
        <Loader2 className="size-3 animate-spin" />
      ) : (
        <Check
          strokeWidth={3}
          className={cn(
            "size-3.5",
            !resolved && "opacity-0 transition-opacity group-hover/box:opacity-30",
          )}
        />
      )}
    </button>
  );
}

function ReportRow({
  report,
  statusPending,
  onToggle,
  onDelete,
  onOpenAttachment,
  onOpen,
}: {
  report: BugReport;
  statusPending: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onOpenAttachment: (index: number) => void;
  onOpen: () => void;
}) {
  const router = useRouter();
  const resolved = report.status === "rijesen";
  const isBug = report.type === "bug";
  const TypeIcon = isBug ? Bug : Lightbulb;

  // Klik bilo gdje na redu otvara tiket, osim na vlastitim kontrolama reda
  // (kvadratić, brisanje, prilozi, linkovi) i kad korisnik označava tekst.
  function handleRowClick(e: MouseEvent<HTMLLIElement>) {
    if ((e.target as HTMLElement).closest("button, a, input, textarea, video")) return;
    if (window.getSelection()?.toString()) return;
    onOpen();
  }

  return (
    <li
      onClick={handleRowClick}
      className="-mx-2 flex cursor-pointer gap-3 border-b px-2 py-4 transition-colors last:border-b-0 hover:bg-accent/40"
    >
      <StatusBox resolved={resolved} pending={statusPending} onToggle={onToggle} />

      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <h3
              className={cn(
                "break-words text-sm font-medium leading-5",
                resolved && "text-muted-foreground line-through decoration-foreground/30",
              )}
            >
              {/* Tastaturom se tiket otvara preko naslova; mišem je klikabilan cijeli red. */}
              <button
                type="button"
                onClick={onOpen}
                className="text-left outline-none hover:underline hover:decoration-foreground/30 hover:underline-offset-2 focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                {report.title}
              </button>
            </h3>
            <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] text-muted-foreground">
              <span
                className={cn(
                  "inline-flex items-center gap-1 font-medium uppercase tracking-wide",
                  isBug && !resolved ? "text-destructive" : "text-foreground/70",
                )}
              >
                <TypeIcon className="size-3" />
                {isBug ? "Bug" : "Prijedlog"}
              </span>
              <span aria-hidden>·</span>
              <span>{report.reportedBy || "Nepoznat"}</span>
              <span aria-hidden>·</span>
              <time
                dateTime={new Date(report.createdAt * 1000).toISOString()}
                className="tabular-nums"
              >
                {timeLabel(new Date(report.createdAt * 1000))}
              </time>
              {report.messageCount > 0 && (
                <>
                  <span aria-hidden>·</span>
                  <span
                    className="inline-flex items-center gap-1 tabular-nums"
                    title={`Poruka u razgovoru: ${report.messageCount}`}
                  >
                    <MessageSquare className="size-3" />
                    {report.messageCount}
                  </span>
                </>
              )}
              {report.pageUrl && (
                <>
                  <span aria-hidden>·</span>
                  <a
                    href={report.pageUrl}
                    title="Otvori stranicu s koje je prijava poslana"
                    onClick={(e) => {
                      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                      e.preventDefault();
                      router.history.push(report.pageUrl);
                    }}
                    className="max-w-full truncate underline decoration-foreground/30 underline-offset-2 outline-none hover:text-foreground hover:decoration-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
                  >
                    {report.pageUrl}
                  </a>
                </>
              )}
            </p>
          </div>

          <button
            type="button"
            onClick={onDelete}
            aria-label={`Obriši prijavu „${report.title}“`}
            title="Obriši prijavu"
            className="-mr-1.5 -mt-1 flex size-7 shrink-0 items-center justify-center text-muted-foreground/60 outline-none transition-colors hover:bg-accent hover:text-destructive focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>

        {report.description && <Description text={report.description} dimmed={resolved} />}

        <AttachmentStrip report={report} onOpen={onOpenAttachment} />

        {resolved && report.resolvedAt && (
          <p className="text-[11px] text-muted-foreground">
            Riješio {report.resolvedBy || "—"} ·{" "}
            {formatDateTime(new Date(report.resolvedAt * 1000).toISOString())}
          </p>
        )}
      </div>
    </li>
  );
}

export function BugReportsPage() {
  const [filter, setFilter] = useState<StatusFilter>("otvoren");
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("sve");
  const [page, setPage] = useState(1);
  const [toDelete, setToDelete] = useState<BugReport | null>(null);
  const [viewing, setViewing] = useState<{ reportId: string; index: number } | null>(null);
  const [ticketId, setTicketId] = useState<string | null>(null);

  const { data, isLoading, isPlaceholderData } = useBugReports(filter, typeFilter, page);
  const setStatus = useSetBugReportStatus();
  const remove = useDeleteBugReport();

  const totalPages = data ? Math.ceil(data.total / PER_PAGE) : 0;
  const counts: Record<StatusFilter, number> | null = data
    ? { ...data.counts, sve: data.counts.otvoren + data.counts.rijesen }
    : null;
  const viewingReport = viewing && data?.items.find((r) => r.id === viewing.reportId);

  // Kad se riješi/obriše zadnja prijava na strani, strana više ne postoji.
  useEffect(() => {
    if (data && !isPlaceholderData && totalPages > 0 && page > totalPages) setPage(totalPages);
  }, [data, isPlaceholderData, totalPages, page]);

  function changeFilter(value: StatusFilter) {
    setFilter(value);
    setPage(1);
  }

  function changeTypeFilter(value: TypeFilter) {
    setTypeFilter(value);
    setPage(1);
  }

  async function handleStatus(id: string, status: BugReportStatus, undoable = true) {
    try {
      await setStatus.mutateAsync({ id, status });
      toast.success(status === "rijesen" ? "Prijava riješena" : "Prijava ponovo otvorena", {
        // Prijava nakon izmjene često nestane iz trenutnog filtera — „Poništi“ je vraća.
        action: undoable
          ? {
              label: "Poništi",
              onClick: () => handleStatus(id, status === "rijesen" ? "otvoren" : "rijesen", false),
            }
          : undefined,
      });
    } catch (e: any) {
      toast.error(e?.message || "Greška pri izmjeni prijave");
    }
  }

  async function handleDelete() {
    if (!toDelete) return;
    try {
      await remove.mutateAsync(toDelete.id);
      toast.success("Prijava obrisana");
      setToDelete(null);
    } catch (e: any) {
      toast.error(e?.message || "Greška pri brisanju prijave");
    }
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-5 p-4">
      <h1 className="text-xl font-semibold">Prijave</h1>

      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b">
        <div className="flex gap-5" role="group" aria-label="Status prijave">
          {STATUS_FILTERS.map((f) => {
            const active = filter === f.value;
            return (
              <button
                key={f.value}
                type="button"
                aria-pressed={active}
                onClick={() => changeFilter(f.value)}
                className={cn(
                  "-mb-px flex items-baseline gap-1.5 border-b-2 pb-2.5 text-sm outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50",
                  active
                    ? "border-foreground font-medium text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground",
                )}
              >
                {f.label}
                {counts && (
                  <span className="text-[11px] font-normal tabular-nums text-muted-foreground">
                    {counts[f.value]}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="mb-2 flex border" role="group" aria-label="Vrsta prijave">
          {TYPE_FILTERS.map((f) => {
            const active = typeFilter === f.value;
            return (
              <button
                key={f.value}
                type="button"
                aria-pressed={active}
                onClick={() => changeTypeFilter(f.value)}
                className={cn(
                  "border-l px-2.5 py-1 text-[11px] outline-none transition-colors first:border-l-0 focus-visible:ring-[3px] focus-visible:ring-ring/50",
                  active
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground",
                )}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4" aria-busy="true" aria-label="Učitavanje prijava">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex gap-3">
              <Skeleton className="size-5 shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-2/5" />
                <Skeleton className="h-3 w-1/4" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : !data || data.items.length === 0 ? (
        <p className="border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">
          {EMPTY_COPY[filter][typeFilter]}
        </p>
      ) : (
        <div
          className={cn(
            "space-y-6 transition-opacity",
            isPlaceholderData && "opacity-50",
          )}
        >
          {groupByDay(data.items).map((group) => (
            <section key={group.label}>
              <h2 className="border-b pb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {group.label}
              </h2>
              <ul>
                {group.items.map((report) => (
                  <ReportRow
                    key={report.id}
                    report={report}
                    statusPending={setStatus.isPending && setStatus.variables?.id === report.id}
                    onToggle={() =>
                      handleStatus(report.id, report.status === "otvoren" ? "rijesen" : "otvoren")
                    }
                    onDelete={() => setToDelete(report)}
                    onOpenAttachment={(index) => setViewing({ reportId: report.id, index })}
                    onOpen={() => setTicketId(report.id)}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {viewing && viewingReport && (
        <AttachmentViewer
          report={viewingReport}
          index={viewing.index}
          onIndexChange={(index) => setViewing({ reportId: viewing.reportId, index })}
          onClose={() => setViewing(null)}
        />
      )}

      <TicketDialog reportId={ticketId} onClose={() => setTicketId(null)} />

      <AlertDialog open={toDelete !== null} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Obrisati prijavu?</AlertDialogTitle>
            <AlertDialogDescription>
              Prijava „{toDelete?.title}“ se briše trajno
              {toDelete && toDelete.attachments.length > 0 ? ", zajedno s prilozima" : ""}. Ako je
              problem riješen, dovoljno je označiti je kao riješenu.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Odustani</AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              onClick={(e) => {
                // Dijalog ostaje otvoren dok brisanje traje; zatvara ga handleDelete.
                e.preventDefault();
                void handleDelete();
              }}
            >
              {remove.isPending && <Loader2 className="size-4 animate-spin" />}
              Obriši prijavu
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
